import React from 'react';
import { Modal, Form, Row, Col, Input, InputNumber, Select, Button, Typography, Grid } from 'antd';
import { IoPersonAddOutline } from 'react-icons/io5';
import { SettingOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const ModalCrearCliente = ({ 
  isModalOpen, 
  setIsModalOpen, 
  form, 
  handleCrearCliente, 
  isEditing, 
  loading, 
  zonas = [], 
  categorias = [],
  clientesSeguros = [],
  clienteSeleccionado,
  configCampos = {},
  onOpenConfig,
  hasPermission
}) => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const isRequerido = (campo, defaultReq = false) => {
    return configCampos[campo]?.requerido ?? defaultReq;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IoPersonAddOutline size={20} style={{ color: '#22BABB' }} />
            <Title level={5} style={{ margin: 0, fontWeight: 700 }}>
              {isEditing ? 'EDITAR CLIENTE' : 'NUEVO CLIENTE'}
            </Title>
          </div>
          {/* Botón para editar el diseño del formulario */}
          {(hasPermission('configuracion:ver_administracion') || hasPermission('clientes:configurar_campos')) && (
            <Button 
              type="link" 
              icon={<SettingOutlined />} 
              onClick={onOpenConfig}
              style={{ padding: 0, fontSize: '13px', color: '#22BABB', fontWeight: 600 }}
            >
              {!isMobile && 'Personalizar formulario'}
            </Button>
          )}
        </div>
      }
      open={isModalOpen}
      onCancel={() => { setIsModalOpen(false); form.resetFields(); }}
      footer={null}
      width={620}
      centered
      styles={{
        body: { padding: isMobile ? '12px 16px' : '16px 20px', maxHeight: '85vh', overflowY: 'auto' },
        mask: { background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)' }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleCrearCliente}
        initialValues={{ limite_credito: 0, status_credito: 'activo' }}
        size={isMobile ? "middle" : "large"}
      >
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="nombre"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>NOMBRES / RAZÓN SOCIAL *</Text>}
              rules={[{ required: true, message: 'El nombre es obligatorio' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: Alexis / Distribuciones S.A.S" allowClear style={{ borderRadius: '8px' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="apellido"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>APELLIDOS {isRequerido('apellido') ? '*' : '(Opcional)'}</Text>}
              rules={[{ required: isRequerido('apellido'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: Ruiz Uribe" allowClear style={{ borderRadius: '8px' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="nombre_comercial"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>NOMBRE COMERCIAL / TIENDA {isRequerido('nombre_comercial') ? '*' : '(Opcional)'}</Text>}
              rules={[{ required: isRequerido('nombre_comercial'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: Abarrotes Don Pedro" allowClear style={{ borderRadius: '8px' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="documento"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>NIT / CC {isRequerido('documento') ? '*' : '(Opcional)'}</Text>}
              rules={[
                { required: isRequerido('documento'), message: 'Requerido' },
                () => ({
                  validator(_, value) {
                    if (!value || !value.trim()) return Promise.resolve();
                    const documentoExiste = clientesSeguros.find(c => c.documento === value.trim());
                    if (documentoExiste && (!isEditing || documentoExiste.id !== clienteSeleccionado?.id)) {
                      return Promise.reject(new Error('Este documento ya pertenece a otro cliente'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: 1216727338" style={{ borderRadius: '8px' }} allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="telefono"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>TELÉFONO {isRequerido('telefono') ? '*' : ''}</Text>}
              rules={[{ required: isRequerido('telefono'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: 3147651337" style={{ borderRadius: '8px' }} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>CORREO ELECTRÓNICO {isRequerido('email') ? '*' : ''}</Text>}
              rules={[{ required: isRequerido('email'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input type="email" placeholder="correo@ejemplo.com" allowClear style={{ borderRadius: '8px' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 0]}>
          <Col xs={24} sm={categorias.length > 0 ? 12 : 24}>
            <Form.Item
              name="zona_id"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>ZONA / RUTA DE ENTREGA {isRequerido('zona_id') ? '*' : ''}</Text>}
              rules={[{ required: isRequerido('zona_id'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Select
                placeholder="Sin zona asignada (General)"
                allowClear
                style={{ borderRadius: '8px' }}
                options={zonas.map(z => ({
                  value: Number(z.id),
                  label: `📍 ${z.nombre} ${z.descripcion ? `— ${z.descripcion}` : ''}`
                }))}
              />
            </Form.Item>
          </Col>

          {categorias.length > 0 && (
            <Col xs={24} sm={12}>
              <Form.Item
                name="categoria_id"
                label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>TIPO DE CLIENTE {isRequerido('categoria_id') ? '*' : '(Opcional)'}</Text>}
                rules={[{ required: isRequerido('categoria_id'), message: 'Requerido' }]}
                style={{ marginBottom: '12px' }}
              >
                <Select
                  placeholder="Ej: Mayorista, Tienda..."
                  allowClear
                  style={{ borderRadius: '8px' }}
                  options={categorias.map(c => ({
                    value: c.id,
                    label: c.nombre
                  }))}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="ciudad"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>CIUDAD / MUNICIPIO {isRequerido('ciudad') ? '*' : ''}</Text>}
              rules={[{ required: isRequerido('ciudad'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: Medellín" allowClear style={{ borderRadius: '8px' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="barrio"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>BARRIO / SECTOR {isRequerido('barrio') ? '*' : ''}</Text>}
              rules={[{ required: isRequerido('barrio'), message: 'Requerido' }]}
              style={{ marginBottom: '12px' }}
            >
              <Input placeholder="Ej: Robledo" allowClear style={{ borderRadius: '8px' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="direccion"
          label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>DIRECCIÓN FÍSICA {isRequerido('direccion') ? '*' : ''}</Text>}
          rules={[{ required: isRequerido('direccion'), message: 'Requerido' }]}
          style={{ marginBottom: '12px' }}
        >
          <Input placeholder="Ej: Crr 87 # 45-12, Barrio Robledo" allowClear style={{ borderRadius: '8px' }} />
        </Form.Item>

        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="limite_credito"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>LÍMITE DE CRÉDITO</Text>}
              style={{ marginBottom: '12px' }}
            >
              <InputNumber
                controls={false}
                prefix="$"
                style={{ width: '100%', borderRadius: '8px' }}
                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={v => v.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="status_credito"
              label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>ESTADO DEL CRÉDITO</Text>}
              style={{ marginBottom: '12px' }}
            >
              <Select style={{ borderRadius: '8px' }}>
                <Select.Option value="activo">Activo (Habilitado)</Select.Option>
                <Select.Option value="suspendido">Suspendido (Bloqueado)</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notas"
          label={<Text strong style={{ fontSize: '12px', color: '#475569' }}>NOTAS / OBSERVACIONES INTERNAS</Text>}
          style={{ marginBottom: '16px' }}
        >
          <Input.TextArea rows={2} placeholder="Ej: Recibe pedidos solo los martes..." allowClear style={{ borderRadius: '8px' }} />
        </Form.Item>

        <Button
          type="primary" htmlType="submit" block loading={loading} disabled={loading}
          style={{
            background: '#22BABB', height: '44px', borderRadius: '10px', fontWeight: '700',
            fontSize: '14px', border: 'none', boxShadow: '0 4px 10px rgba(34, 186, 187, 0.25)'
          }}
        >
          {isEditing ? 'ACTUALIZAR CLIENTE' : 'GUARDAR NUEVO CLIENTE'}
        </Button>
      </Form>
    </Modal>
  );
};

export default ModalCrearCliente;